import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

export interface Question {
  id: number;
  text: string;
  question_type: 'open_text' | 'rating' | 'checklist' | 'rating_text';
  is_required: boolean;
  order_index: number;
  options: string[] | null;
  rating_scale: number;
}

interface Assignment {
  id: number;
  template_name: string;
  template_description?: string | null;
  subject_name: string | null;
  status: string;
  questions: Question[];
}

export type Answers = Record<number, {
  answer_text?: string;
  answer_rating?: number;
  answer_checklist?: string[];
}>;

function StarRating({ value, scale, onChange, disabled }: {
  value: number | undefined;
  scale: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  // Numeric scale: a row of 1..scale buttons (1 = lowest). Kept under the old name so callers need no change.
  return (
    <div>
      <div className="scale-group" role="radiogroup">
        {Array.from({ length: scale }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            className={`scale-btn ${value === n ? 'active' : ''}`}
            onClick={() => !disabled && onChange(n)}
            disabled={disabled}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="scale-legend text-muted">
        <span>1 = הנמוך ביותר</span>
        <span>{value ? `נבחר: ${value} מתוך ${scale}` : ''}</span>
        <span>{scale} = הגבוה ביותר</span>
      </div>
    </div>
  );
}


/** Renders the question list; shared by the respondent page and the admin preview. */
export function QuestionBlocks({ questions, answers, setAnswer, toggleChecklist, isReadonly }: {
  questions: Question[];
  answers: Answers;
  setAnswer: (questionId: number, field: string, value: any) => void;
  toggleChecklist: (questionId: number, option: string) => void;
  isReadonly: boolean;
}) {
  return (
    <>
      {questions.map((q, idx) => (
        <div key={q.id} className="question-block">
          <div className="question-text">
            {idx + 1}. {q.text}
            {q.is_required && <span className="required-mark">*</span>}
          </div>

          {q.question_type === 'open_text' && (
            <textarea
              className="form-input"
              value={answers[q.id]?.answer_text || ''}
              onChange={e => setAnswer(q.id, 'answer_text', e.target.value)}
              placeholder="כתוב את תשובתך כאן..."
              disabled={isReadonly}
              rows={4}
            />
          )}

          {q.question_type === 'rating' && (
            <StarRating
              value={answers[q.id]?.answer_rating}
              scale={q.rating_scale}
              onChange={v => setAnswer(q.id, 'answer_rating', v)}
              disabled={isReadonly}
            />
          )}

          {q.question_type === 'rating_text' && (
            <div>
              <StarRating
                value={answers[q.id]?.answer_rating}
                scale={q.rating_scale}
                onChange={v => setAnswer(q.id, 'answer_rating', v)}
                disabled={isReadonly}
              />
              <textarea
                className="form-input"
                style={{ marginTop: '10px' }}
                value={answers[q.id]?.answer_text || ''}
                onChange={e => setAnswer(q.id, 'answer_text', e.target.value)}
                placeholder={q.is_required ? 'פרט/י – הסבר לדירוג (חובה)...' : 'הסבר / הערות לדירוג (לא חובה)...'}
                disabled={isReadonly}
                rows={3}
              />
            </div>
          )}

          {q.question_type === 'checklist' && q.options && (
            <div>
              {q.options.map(option => (
                <label key={option} className="checklist-item">
                  <input
                    type="checkbox"
                    checked={(answers[q.id]?.answer_checklist || []).includes(option)}
                    onChange={() => !isReadonly && toggleChecklist(q.id, option)}
                    disabled={isReadonly}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export default function Questionnaire() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: a } = await api.get(`/assignments/my`);
        const found = a.find((x: any) => x.id === parseInt(id!));
        if (!found) { navigate('/dashboard'); return; }
        setAssignment(found);

        if (found.status !== 'pending') {
          const { data: saved } = await api.get(`/responses/assignment/${id}`);
          const initialAnswers: Answers = {};
          for (const [qId, resp] of Object.entries(saved)) {
            initialAnswers[parseInt(qId)] = resp as any;
          }
          setAnswers(initialAnswers);
        }
      } catch (err) {
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const setAnswer = (questionId: number, field: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: value }
    }));
  };

  const toggleChecklist = (questionId: number, option: string) => {
    const current = answers[questionId]?.answer_checklist || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    setAnswer(questionId, 'answer_checklist', updated);
  };

  const buildPayload = (submit: boolean) => ({
    submit,
    answers: assignment!.questions.map(q => ({
      question_id: q.id,
      answer_text: answers[q.id]?.answer_text || null,
      answer_rating: answers[q.id]?.answer_rating || null,
      answer_checklist: answers[q.id]?.answer_checklist || null,
    }))
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/responses/assignment/${id}`, buildPayload(false));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    // Client-side required validation
    const missing = assignment!.questions.filter(q => {
      if (!q.is_required) return false;
      const ans = answers[q.id];
      if (!ans) return true;
      if (q.question_type === 'open_text' && !ans.answer_text?.trim()) return true;
      if ((q.question_type === 'rating' || q.question_type === 'rating_text') && !ans.answer_rating) return true;
      if (q.question_type === 'rating_text' && !ans.answer_text?.trim()) return true;
      return false;
    });
    if (missing.length > 0) {
      setError(`יש למלא את כל השדות המסומנים כחובה (${missing.length} שדות חסרים)`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    try {
      await api.post(`/responses/assignment/${id}`, buildPayload(true));
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שגיאה בהגשה');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" />טוען שאלון...</div>;
  if (!assignment) return null;

  if (submitted) {
    return (
      <div className="page page-narrow" style={{ textAlign: 'center', paddingTop: '64px' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
        <h1>השאלון הוגש בהצלחה!</h1>
        <p className="text-muted" style={{ margin: '12px 0 28px' }}>
          תודה על מילוי השאלון. תשובותיך נשמרו.
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
          חזור ללוח הבקרה
        </button>
      </div>
    );
  }

  const isReadonly = assignment.status === 'submitted';

  return (
    <div className="page page-narrow">
      <div className="page-header">
        <div style={{ marginBottom: '4px' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/dashboard')}>
            ← חזור
          </button>
        </div>
        <h1 style={{ marginTop: '12px' }}>{assignment.template_name}</h1>
        {assignment.subject_name && (
          <p className="text-muted">עבור: {assignment.subject_name}</p>
        )}
        {assignment.template_description && (
          <div className="alert alert-info" style={{ marginTop: '12px', whiteSpace: 'pre-wrap', textAlign: 'right' }}>
            {assignment.template_description}
          </div>
        )}
        {isReadonly && (
          <div className="alert alert-info" style={{ marginTop: '12px' }}>
            שאלון זה כבר הוגש ואינו ניתן לעריכה.
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <QuestionBlocks questions={assignment.questions} answers={answers} setAnswer={setAnswer} toggleChecklist={toggleChecklist} isReadonly={isReadonly} />

      {!isReadonly && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
            שמור כטיוטה
          </button>
          <button className="btn btn-success btn-lg" onClick={handleSubmit} disabled={saving}>
            {saving ? 'שולח...' : 'הגש שאלון'}
          </button>
        </div>
      )}
    </div>
  );
}
